import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Users,
  Wrench,
  Package,
  Shield,
  Search,
  RefreshCw,
  Star,
  Mail,
  Phone,
  Building,
  CheckCircle,
  XCircle,
  AlertTriangle
} from "lucide-react";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

export default function DualRoleManagement({ token }) {
  const [loading, setLoading] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [dualRoleCount, setDualRoleCount] = useState(0);

  useEffect(() => {
    fetchEntities();
  }, [token]);

  const fetchEntities = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/admin/dual-role/entities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContractors(data.contractors || []);
        setSuppliers(data.suppliers || []);
        setDualRoleCount(data.dual_role_count || 0);
      }
    } catch (error) {
      console.error("Error fetching entities:", error);
      toast.error("Failed to load entities");
    }
    setLoading(false);
  };

  const handleToggleRole = async (entity, entityType, enableRole, action) => {
    setPendingAction({ entity, entityType, enableRole, action });
    setSelectedEntity(entity);
    setShowConfirmModal(true);
  };

  const confirmToggle = async () => {
    if (!pendingAction) return;
    
    const { entity, entityType, enableRole, action } = pendingAction;
    
    try {
      const response = await fetch(`${API}/api/admin/dual-role/toggle`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          entity_id: entity.id,
          entity_type: entityType,
          enable_role: enableRole,
          action: action
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchEntities();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update role");
      }
    } catch (error) {
      toast.error("Failed to update role");
    }
    
    setShowConfirmModal(false);
    setPendingAction(null);
    setSelectedEntity(null);
  };

  const filterEntities = (list) => {
    if (!searchTerm) return list;
    const term = searchTerm.toLowerCase();
    return list.filter(e => 
      e.company_name?.toLowerCase().includes(term) ||
      e.name?.toLowerCase().includes(term) ||
      e.email?.toLowerCase().includes(term)
    );
  };

  const EntityCard = ({ entity, entityType }) => {
    const isDualRole = entity.is_dual_role;
    const hasContractorRole = entity.roles?.includes("CONTRACTOR") || entityType === "contractor";
    const hasSupplierRole = entity.roles?.includes("SUPPLIER") || entityType === "supplier";
    
    return (
      <Card className={`border ${isDualRole ? 'border-purple-300 bg-purple-50/30' : 'border-slate-200'}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-slate-800">
                  {entity.company_name || entity.name}
                </span>
                {isDualRole && (
                  <Badge className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs">
                    <Star className="w-3 h-3 mr-1" />
                    Dual-Role
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                <span className="flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  {entity.email}
                </span>
                {entity.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-3 h-3" />
                    {entity.phone}
                  </span>
                )}
              </div>
              
              {/* Role Toggles */}
              <div className="space-y-3 pt-3 border-t border-slate-200">
                <h4 className="text-xs font-medium text-slate-600 uppercase tracking-wide">Role Elevation</h4>
                
                {/* Supplier Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className={`w-4 h-4 ${hasSupplierRole ? 'text-green-600' : 'text-slate-400'}`} />
                    <Label className="text-sm">Supplier Access</Label>
                    {hasSupplierRole && (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                        Active
                      </Badge>
                    )}
                  </div>
                  <Switch
                    checked={hasSupplierRole}
                    onCheckedChange={(checked) => {
                      if (entityType === "supplier" && !checked) return; // Can't remove primary role
                      handleToggleRole(entity, entityType, "supplier", checked ? "add" : "remove");
                    }}
                    disabled={entityType === "supplier"}
                    className={entityType === "supplier" ? "opacity-50" : ""}
                  />
                </div>
                <p className="text-xs text-slate-500 ml-6">
                  Respond to Backorder RFQs, upload parts catalogs, submit quotes
                </p>
                
                {/* Contractor Toggle */}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <Wrench className={`w-4 h-4 ${hasContractorRole ? 'text-blue-600' : 'text-slate-400'}`} />
                    <Label className="text-sm">Contractor Access</Label>
                    {hasContractorRole && (
                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                        Active
                      </Badge>
                    )}
                  </div>
                  <Switch
                    checked={hasContractorRole}
                    onCheckedChange={(checked) => {
                      if (entityType === "contractor" && !checked) return; // Can't remove primary role
                      handleToggleRole(entity, entityType, "contractor", checked ? "add" : "remove");
                    }}
                    disabled={entityType === "contractor"}
                    className={entityType === "contractor" ? "opacity-50" : ""}
                  />
                </div>
                <p className="text-xs text-slate-500 ml-6">
                  Receive maintenance job assignments, upload service photos, submit certificates
                </p>
              </div>
              
              {/* Linked Entity Info */}
              {(entity.linked_supplier_id || entity.linked_contractor_id) && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-500">
                    {entity.linked_supplier_id && (
                      <span>Linked Supplier ID: <code className="bg-slate-100 px-1 rounded">{entity.linked_supplier_id}</code></span>
                    )}
                    {entity.linked_contractor_id && (
                      <span>Linked Contractor ID: <code className="bg-slate-100 px-1 rounded">{entity.linked_contractor_id}</code></span>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Shield className="w-6 h-6 text-purple-600" />
            Dual-Role Management
          </h2>
          <p className="text-slate-600 mt-1">
            Grant entities both Supplier and Contractor authorities
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-purple-100 text-purple-800 text-sm px-3 py-1">
            {dualRoleCount} Dual-Role Entities
          </Badge>
          <Button variant="outline" onClick={fetchEntities} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
        <Input
          placeholder="Search by name, company, or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tabs for Contractors and Suppliers */}
      <Tabs defaultValue="contractors" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="contractors" className="flex items-center gap-2">
            <Wrench className="w-4 h-4" />
            Contractors ({contractors.length})
          </TabsTrigger>
          <TabsTrigger value="suppliers" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Suppliers ({suppliers.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contractors" className="mt-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filterEntities(contractors).map(contractor => (
              <EntityCard key={contractor.id} entity={contractor} entityType="contractor" />
            ))}
            {filterEntities(contractors).length === 0 && (
              <p className="text-slate-500 col-span-full text-center py-8">
                No contractors found
              </p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="suppliers" className="mt-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filterEntities(suppliers).map(supplier => (
              <EntityCard key={supplier.id} entity={supplier} entityType="supplier" />
            ))}
            {filterEntities(suppliers).length === 0 && (
              <p className="text-slate-500 col-span-full text-center py-8">
                No suppliers found
              </p>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Role Change
            </DialogTitle>
            <DialogDescription>
              {pendingAction?.action === "add" ? (
                <>
                  You are about to grant <strong>{pendingAction?.enableRole}</strong> access to{" "}
                  <strong>{selectedEntity?.company_name || selectedEntity?.name}</strong>.
                  <br /><br />
                  This will:
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Create a linked {pendingAction?.enableRole} profile</li>
                    <li>Send email notification about the upgrade</li>
                    <li>Enable {pendingAction?.enableRole === "supplier" ? "Supply Chain" : "Maintenance"} dashboard access</li>
                  </ul>
                </>
              ) : (
                <>
                  You are about to remove <strong>{pendingAction?.enableRole}</strong> access from{" "}
                  <strong>{selectedEntity?.company_name || selectedEntity?.name}</strong>.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmToggle}
              className={pendingAction?.action === "add" ? "bg-purple-600 hover:bg-purple-700" : "bg-red-600 hover:bg-red-700"}
            >
              {pendingAction?.action === "add" ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Grant Access
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Remove Access
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
